import * as React from 'react';
import {
  IDetailsListProps
} from 'office-ui-fabric-react/lib/DetailsList';
import {
  IDetailsColumnProps
} from 'office-ui-fabric-react/lib/components/DetailsList/DetailsColumn';
import {
  DirectionalHint
} from 'office-ui-fabric-react/lib/ContextualMenu';
import { IRenderFunction } from 'office-ui-fabric-react/lib/Utilities';
import { Engagement } from '@ms/telemetry-manager/lib/events/Engagement.event';
import { Fragment } from '../../utilities/components/Fragment';
import { IItemColumn } from '../../models/schema/IItemColumn';
import { ICommand } from '../../models/commands/ICommand';
import { IComponentDecorator, IDecoratedProps, IDecoratedComponentType } from '../../utilities/components/Decorators';
import { asAsync } from '../../utilities/components/Async';
import { afterPageLoad } from './afterPageLoad';
import ColumnDivider from '../columnDivider/ColumnDivider';
import { withTelemetry, ITelemetryConsumerStateProps } from '../telemetry/TelemetryProvider';

const ActionContextMenu = afterPageLoad()(asAsync(async () => (
  await import(/* webpackChunkName: 'deferredcomponents' */'../DeferredComponents')
).ActionContextMenu));

/**
 * The required props for the component produced by the withColumnCommands decorator.
 * @public
 */
export interface ICommandColumnsProps {
  /**
   * The list of columns passed to the DetailsList.
   * These can have additional fields such as 'headerCommand', which will be used
   * to implement the column-click behavior.
   */
  columns?: IItemColumn[];
  /**
   * The key for the item set used to bind the list data.
   * This is used to indicate when the target list contents have changed
   * and the command set should be re-evaluated.
   */
  setKey?: string;
}

/**
 * Decorator for a DetailsList which consumes a list of augmented columns
 * and implements the column-click behaviors using the provided header commands.
 * @public
 */
export function withCommandColumns(): IComponentDecorator<Pick<IDetailsListProps, 'columns'>, ICommandColumnsProps> {
  return <P extends Pick<IDetailsListProps, 'columns'>>(
    component: React.ComponentType<P>
  ): React.ComponentType<IDecoratedProps<P, Pick<IDetailsListProps, 'columns'>, ICommandColumnsProps>> => {
    const Component = component;

    type IProps = IDecoratedProps<P, Pick<IDetailsListProps, 'columns'>, ICommandColumnsProps & ITelemetryConsumerStateProps>;

    interface IWithCommandColumnsState {
      contextMenu?: {
        target?: HTMLElement;
        commands: ICommand[];
      };
      addIconVisibleForIndex: number;
      contextMenuIsOpen: boolean;
    }

    /**
     * Can we get some comments about the purpose of this class, and comments about
     * each method and any other comments that will help?
     */
    class WithCommandColumns extends React.PureComponent<IProps, IWithCommandColumnsState> {
      constructor(props: IProps) {
        super(props);

        this.state = {
          addIconVisibleForIndex: -1,
          contextMenuIsOpen: false
        };
      }

      public componentWillReceiveProps(nextProps: IProps): void {
        if ((this.state.contextMenuIsOpen && nextProps.columns && this._isColumnOrderChanged(nextProps.columns))
          || (nextProps.setKey !== this.props.setKey)) {
          this.setState({
            contextMenu: undefined,
            contextMenuIsOpen: false
          });
        }
      }

      public render(): JSX.Element {
        const {
          columns = [],
          ...detailsListProps
        } = this.props as Pick<IDetailsListProps, 'columns'>;

        const {
          contextMenu
        } = this.state;

        const finalColumns = columns.map((column: IItemColumn) => {
          return {
            ...column,
            onRenderDivider: column.dividerCommand ? this._onRenderDivider : undefined,
            onColumnClick: column.headerCommand ? this._onColumnClick : undefined
          };
        });

        return (
          <Fragment>
            <Component
              { ...detailsListProps }
              columns={ finalColumns }
            />
            {
              contextMenu ? (
                <ActionContextMenu
                  target={ contextMenu.target }
                  commands={ contextMenu.commands }
                  directionalHint={ DirectionalHint.bottomLeftEdge }
                  isOpen={ this.state.contextMenuIsOpen }
                  onDismiss={ this._onDismissContextMenu }
                />
              ) : null
            }
          </Fragment>
        );
      }

      private _onDividerClick = (event: React.MouseEvent<HTMLElement>, column: IItemColumn, columnIndex: number) => {
        const target = event.target as HTMLElement;

        if (column && column.dividerCommand && column.dividerCommand.subMenuProps) {
          this.setState({
            contextMenu: {
              target: target,
              commands: column.dividerCommand.subMenuProps.items
            },
            addIconVisibleForIndex: columnIndex,
            contextMenuIsOpen: true
          });
        }
      }

      private _onRenderDivider = (
        detailsColumnProps?: IDetailsColumnProps,
        defaultRender?: IRenderFunction<IDetailsColumnProps>
      ): JSX.Element | null => {
        if (!detailsColumnProps) {
          return null;
        }
        const { column, columnIndex } = detailsColumnProps;
        const engagement = this.props.telemetry(Engagement);
        return (
          <ColumnDivider
            column={ column }
            engagement={ engagement }
            columnIndex={ columnIndex }
            defaultRender={ defaultRender }
            onAddNewColumn={ this._onDividerClick }
            addIconVisibleForIndex={ this.state.addIconVisibleForIndex }
          />
        );
      }

      private _onColumnClick = (ev?: React.MouseEvent<HTMLElement>, column?: IItemColumn): void => {
        const target = ev && ev.target as HTMLElement;

        if (column && column.headerCommand && column.headerCommand.subMenuProps) {
          this.setState({
            contextMenu: {
              target: target,
              commands: column.headerCommand.subMenuProps.items
            },
            contextMenuIsOpen: true
          });
        }
      }

      private _onDismissContextMenu = (): void => {
        this.setState({
          addIconVisibleForIndex: -1,
          contextMenuIsOpen: false
        });
      }

      private _isColumnOrderChanged = (newPropsColumns: IItemColumn[]): boolean => {
        if (this.props.columns) {
          for (let i = 0; i < newPropsColumns.length; i++) {
            if (newPropsColumns[i].key !== this.props.columns[i].key) {
              return true;
            }
          }
        }
        return false;
      }
    }

    const WithCommandColumnsAndTelemetry = withTelemetry()(WithCommandColumns as {} as React.ComponentType<ITelemetryConsumerStateProps>);

    return WithCommandColumnsAndTelemetry as {} as IDecoratedComponentType<P, Pick<IDetailsListProps, 'columns'>, ICommandColumnsProps>;
  };
}
